// frontend/src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, UserLogin, UserRegistration, AuthTokens } from '../interfaces';
import axios from 'axios';
import { API_BASE_URL, buildApiUrl } from '../config/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: UserLogin) => Promise<void>;
  register: (data: UserRegistration) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  isAuthenticated: boolean;
  isApproved: boolean;
  canManageUsers: boolean;
  canDelete: boolean;
  canEditEquipment: boolean;
  canEditEvents: boolean;
  canOnlyView: boolean;
  isAdmin: boolean;
  isProduction: boolean;
  isWarehouse: boolean;
  isTeamLeader: boolean;
  isTechnician: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** מונע מספר בקשות refresh במקביל (כולן חולקות אותו Promise) */
let refreshAccessPromise: Promise<string> | null = null;

function isAuthRefreshRequest(config: { url?: string } | undefined): boolean {
  const url = config?.url ?? '';
  return typeof url === 'string' && url.includes('auth/refresh');
}

async function refreshAccessToken(): Promise<string> {
  const storedRefresh = localStorage.getItem('refresh_token');
  if (!storedRefresh) {
    throw new Error('No refresh token');
  }
  const response = await axios.post<{ access: string; refresh?: string }>(
    buildApiUrl('users/auth/refresh/'),
    { refresh: storedRefresh },
    { timeout: 15000 }
  );
  const { access, refresh: newRefresh } = response.data;
  localStorage.setItem('access_token', access);
  if (newRefresh) {
    localStorage.setItem('refresh_token', newRefresh);
  }
  return access;
}

function clearSessionAndRedirectToLogin() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  window.location.replace(`${base}/login?relogin=1`);
}

// הגדרת axios interceptor לטיפול בטוקנים
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// הגדרת axios interceptor לטיפול ברענון טוקנים
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    if (isAuthRefreshRequest(originalRequest)) {
      clearSessionAndRedirectToLogin();
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    if (!localStorage.getItem('refresh_token')) {
      clearSessionAndRedirectToLogin();
      return Promise.reject(error);
    }

    try {
      if (!refreshAccessPromise) {
        refreshAccessPromise = refreshAccessToken().finally(() => {
          refreshAccessPromise = null;
        });
      }
      const access = await refreshAccessPromise;
      originalRequest.headers.Authorization = `Bearer ${access}`;
      return axios(originalRequest);
    } catch {
      clearSessionAndRedirectToLogin();
      return Promise.reject(error);
    }
  }
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const savedUser = localStorage.getItem('user');
      const accessToken = localStorage.getItem('access_token');

      if (!savedUser || !accessToken) {
        if (savedUser && !accessToken) {
          localStorage.removeItem('user');
        }
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        const parsedUser = JSON.parse(savedUser) as User;
        if (!cancelled) setUser(parsedUser);
      } catch {
        localStorage.removeItem('user');
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        const { data } = await axios.get<User>(buildApiUrl('users/users/me/'), {
          timeout: 12000,
        });
        if (cancelled) return;
        localStorage.setItem('user', JSON.stringify(data));
        setUser(data);
      } catch (e) {
        if (cancelled) return;
        if (axios.isAxiosError(e)) {
          const status = e.response?.status;
          if (status === 401 || status === 403) {
            clearSessionAndRedirectToLogin();
            return;
          }
          if (!e.response) {
            clearSessionAndRedirectToLogin();
            return;
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (credentials: UserLogin) => {
    try {
      const response = await axios.post<AuthTokens>(buildApiUrl('users/auth/login/'), credentials);
      const { access, refresh, user: userData } = response.data;

      // שמירת טוקנים
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      // קבלת פרטי המשתמש
      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      } else {
        // אם אין פרטי משתמש בתשובה, נבקש אותם
        const userResponse = await axios.get<User>(buildApiUrl('users/users/me/'));
        localStorage.setItem('user', JSON.stringify(userResponse.data));
        setUser(userResponse.data);
      }
    } catch (error: any) {
      // העברת כל השגיאה בצורה מלאה כדי שהקומפוננטה יוכל לטפל בה
      // במקום ליצור שגיאה חדשה, נשמור את השגיאה המקורית
      const errorObj = error.response?.data || {};
      
      // יצירת שגיאה מותאמת עם כל הפרטים
      const customError: any = new Error(errorObj.detail || 'שגיאה בהתחברות');
      customError.response = {
        data: errorObj
      };
      
      throw customError;
    }
  };

  const register = async (data: UserRegistration): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await axios.post(buildApiUrl('users/users/'), data);
      return {
        success: true,
        message: response.data.message || 'נרשמת בהצלחה! חשבונך ממתין לאישור מנהל.'
      };
    } catch (error: any) {
      const message = error.response?.data?.username?.[0] || 
                     error.response?.data?.email?.[0] || 
                     error.response?.data?.password?.[0] ||
                     error.response?.data?.detail || 
                     'שגיאה בהרשמה';
      return {
        success: false,
        message
      };
    }
  };

  const refreshToken = async () => {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post<{ access: string; refresh?: string }>(
        buildApiUrl('users/auth/refresh/'),
        { refresh },
        { timeout: 15000 }
      );

      localStorage.setItem('access_token', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refresh_token', response.data.refresh);
      }
    } catch (error) {
      logout();
      throw error;
    }
  };

  const isAuthenticated = !!user && !!localStorage.getItem('access_token');
  const isApproved = user?.is_approved ?? false;
  const canManageUsers = user?.role?.name === 'ADMIN';
  
  // הרשאות נוספות לפי תפקיד
  const canDelete = user?.role?.can_delete ?? false;
  const canEditEquipment = user?.role?.can_edit_equipment ?? false;
  const canEditEvents = user?.role?.can_edit_events ?? false;
  const canOnlyView = user?.role?.can_only_view ?? false;
  const isAdmin = user?.role?.name === 'ADMIN';
  const isProduction = user?.role?.name === 'PRODUCTION';
  const isWarehouse = user?.role?.name === 'WAREHOUSE';
  const isTeamLeader = user?.role?.name === 'TEAM_LEADER';
  const isTechnician = user?.role?.name === 'TECHNICIAN';

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refreshToken,
        isAuthenticated,
        isApproved,
        canManageUsers,
        canDelete,
        canEditEquipment,
        canEditEvents,
        canOnlyView,
        isAdmin,
        isProduction,
        isWarehouse,
        isTeamLeader,
        isTechnician
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
